import { useState } from 'react';
import { Check, Pencil, Plus, Star, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/cn';
import {
  LARGO_DEL_NOMBRE,
  TOPE_DE_VISTAS,
  aplicarVista,
  borrarVista,
  cabeOtraVista,
  guardarVista,
  marcarDeEntrada,
  nombreDeVista,
  renombrarVista,
  vistaAplicada,
  vistaLlamada,
  type PrefCartera,
  type VistaGuardada,
} from '@/lib/pref-cartera';
import { VISTAS } from './vistas-de-clientes';

/**
 * «MIS VISTAS» (tanda 6; «que el cliente pueda convertir su parte visual a su imagen y
 * semejanza»): la pantalla de Clientes armada a su gusto (vista, agrupación, orden, columnas,
 * densidad, filtro y etapa) y guardada con un nombre para volver a ella con un toque.
 *
 * Es el contenido del panel que abre el botón «Mis vistas» de la barra. No guarda nada por su
 * cuenta: cada toque es una operación PURA de `lib/pref-cartera` sobre la preferencia `cartera`,
 * que es la que viaja al servidor. Las preguntas («¿la reemplazas?», «¿borrarla?») se hacen aquí.
 */
export interface PanelDeVistasProps {
  pref: PrefCartera;
  onPref: (cambio: (antes: PrefCartera) => PrefCartera) => void;
  /** Para las pruebas: el id de una vista nueva. */
  generarId?: () => string;
}

const idNuevo = () => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

const ICONO = 'grid h-11 w-11 shrink-0 place-items-center rounded-md text-slate-500 transition-colors hover:bg-slate-200/70 hover:text-slate-800 md:h-8 md:w-8 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-100';
const FOCO = 'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand-600';
const CAMPO =
  'h-11 min-w-0 flex-1 rounded-md border border-surface-border bg-surface px-3 text-base text-slate-900 placeholder:text-slate-400 ' +
  'focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 md:h-9 md:text-sm dark:border-slate-700 dark:text-slate-100';

export function PanelDeVistas({ pref, onPref, generarId = idNuevo }: PanelDeVistasProps) {
  // null = el campo está cerrado; un texto (aunque vacío) = se está escribiendo el nombre.
  const [nueva, setNueva] = useState<string | null>(null);
  const [renombrando, setRenombrando] = useState<{ id: string; nombre: string } | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const puesta = vistaAplicada(pref);
  const laPuesta = puesta && pref.guardadas.find((g) => g.id === puesta.id);
  const cabe = cabeOtraVista(pref);

  const guardar = (nombre: string) => {
    const ya = vistaLlamada(pref, nombre);
    if (ya && !window.confirm(`Ya tienes una vista llamada «${ya.nombre}». ¿La reemplazas con lo que ves ahora?`)) return;
    onPref((p) => guardarVista(p, nombre, generarId));
    setNueva(null);
  };
  const renombrar = ({ id, nombre }: { id: string; nombre: string }) => {
    const otra = vistaLlamada(pref, nombre);
    if (otra && otra.id !== id) return setAviso(`Ya tienes otra vista llamada «${otra.nombre}».`);
    onPref((p) => renombrarVista(p, id, nombre));
    setRenombrando(null);
    setAviso(null);
  };
  const borrar = (g: VistaGuardada) => {
    if (window.confirm(`¿Borrar la vista «${g.nombre}»? La pantalla se queda como la tienes ahora.`)) onPref((p) => borrarVista(p, g.id));
  };

  return (
    <div className="space-y-3">
      {laPuesta && puesta?.modificada && (
        <div className="flex items-center gap-2 rounded-lg bg-amber-50 py-1.5 pl-3 pr-1.5 text-xs text-amber-900 dark:bg-amber-900/25 dark:text-amber-100">
          <p className="min-w-0 flex-1 leading-snug">{`Le cambiaste algo a «${laPuesta.nombre}».`}</p>
          <Button size="sm" className="min-h-[44px] shrink-0 md:min-h-0" onClick={() => onPref((p) => guardarVista(p, laPuesta.nombre, generarId))}>
            Guardar cambios
          </Button>
        </div>
      )}

      {pref.guardadas.length === 0 ? (
        <p className="px-1 text-sm leading-relaxed text-slate-600 dark:text-slate-300">
          Todavía no has guardado ninguna. Arma la pantalla a tu gusto (la vista, el filtro, el orden, las columnas) y guárdala con un nombre para volver a ella con un toque.
        </p>
      ) : (
        <ul className="space-y-0.5">
          {pref.guardadas.map((g) => {
            const esLaPuesta = puesta?.id === g.id;
            const modificada = esLaPuesta && Boolean(puesta?.modificada);
            const deEntrada = pref.deEntrada === g.id;
            const vista = VISTAS.find((v) => v.id === g.config.vista)!;
            const Icono = vista.icono;
            if (renombrando?.id === g.id) {
              return (
                <li key={g.id}>
                  <form
                    className="flex items-center gap-2 py-0.5"
                    onSubmit={(e) => {
                      e.preventDefault();
                      renombrar(renombrando);
                    }}
                  >
                    <input
                      autoFocus
                      aria-label={`Nuevo nombre de ${g.nombre}`}
                      maxLength={LARGO_DEL_NOMBRE}
                      value={renombrando.nombre}
                      onChange={(e) => setRenombrando({ id: g.id, nombre: e.target.value })}
                      onKeyDown={(e) => {
                        if (e.key !== 'Escape') return;
                        // Escape deja de renombrar; no cierra el panel entero.
                        e.stopPropagation();
                        setRenombrando(null);
                        setAviso(null);
                      }}
                      className={CAMPO}
                    />
                    <Button type="submit" size="sm" variant="outline" className="min-h-[44px] md:min-h-0" disabled={!nombreDeVista(renombrando.nombre)}>
                      Listo
                    </Button>
                  </form>
                </li>
              );
            }
            return (
              <li key={g.id} data-vista={g.id} className={cn('flex items-center rounded-lg transition-colors', esLaPuesta ? 'bg-brand-50 dark:bg-brand-900/30' : 'hover:bg-slate-100 dark:hover:bg-slate-800')}>
                <button
                  type="button"
                  aria-current={esLaPuesta ? 'true' : undefined}
                  title={esLaPuesta && !modificada ? 'Es la que estás viendo' : 'Ver a tus clientes así'}
                  onClick={() => onPref((p) => aplicarVista(p, g.id))}
                  className={cn('flex min-h-[44px] min-w-0 flex-1 items-center gap-2.5 rounded-lg py-1 pl-2.5 pr-1 text-left', FOCO)}
                >
                  <Icono aria-hidden className={cn('h-4 w-4 shrink-0', esLaPuesta ? 'text-brand-700 dark:text-brand-300' : 'text-slate-500 dark:text-slate-400')} />
                  <span className="min-w-0 flex-1">
                    <span className={cn('block truncate text-sm font-medium', esLaPuesta ? 'text-brand-800 dark:text-brand-100' : 'text-slate-900 dark:text-slate-100')}>
                      {g.nombre}
                    </span>
                    {/* «modificada» va en la línea de abajo: junto al nombre, lo recortaba. */}
                    <span className="block truncate text-xs text-slate-500 dark:text-slate-400">
                      {vista.etiqueta}
                      {modificada && <span className="font-medium text-amber-700 dark:text-amber-400"> · modificada</span>}
                      {deEntrada && ' · abre con esta'}
                    </span>
                  </span>
                  {esLaPuesta && <Check aria-hidden className="h-4 w-4 shrink-0 text-brand-700 dark:text-brand-300" />}
                </button>
                <button
                  type="button"
                  aria-pressed={deEntrada}
                  aria-label={`Abrir con esta: ${g.nombre}`}
                  title={deEntrada ? 'Clientes abre con esta. Toca para quitarla.' : 'Abrir con esta'}
                  onClick={() => onPref((p) => marcarDeEntrada(p, deEntrada ? null : g.id))}
                  className={cn(ICONO, FOCO, deEntrada && 'text-amber-500 hover:text-amber-600 dark:text-amber-400')}
                >
                  <Star className="h-4 w-4" fill={deEntrada ? 'currentColor' : 'none'} />
                </button>
                <button type="button" aria-label={`Renombrar ${g.nombre}`} title="Renombrar" onClick={() => setRenombrando({ id: g.id, nombre: g.nombre })} className={cn(ICONO, FOCO)}>
                  <Pencil className="h-4 w-4" />
                </button>
                <button type="button" aria-label={`Borrar ${g.nombre}`} title="Borrar" onClick={() => borrar(g)} className={cn(ICONO, FOCO, 'hover:text-red-600 dark:hover:text-red-400')}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {aviso && (
        <p role="alert" className="px-1 text-xs font-medium text-red-700 dark:text-red-400">
          {aviso}
        </p>
      )}

      <div className="border-t border-surface-border pt-3">
        {nueva !== null ? (
          <form
            className="flex items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              guardar(nueva);
            }}
          >
            <input
              autoFocus
              aria-label="Nombre de la vista"
              placeholder="Ponle un nombre"
              maxLength={LARGO_DEL_NOMBRE}
              value={nueva}
              onChange={(e) => setNueva(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== 'Escape') return;
                e.stopPropagation();
                setNueva(null);
              }}
              className={CAMPO}
            />
            <Button type="submit" size="sm" className="min-h-[44px] md:min-h-0" disabled={!nombreDeVista(nueva)}>
              Guardar
            </Button>
          </form>
        ) : cabe ? (
          <button
            type="button"
            onClick={() => setNueva('')}
            className={cn(
              'flex min-h-[44px] w-full items-center gap-2 rounded-lg px-2.5 text-sm font-medium text-brand-700 transition-colors hover:bg-brand-50 md:min-h-[36px] dark:text-brand-300 dark:hover:bg-brand-900/30',
              FOCO,
            )}
          >
            <Plus aria-hidden className="h-4 w-4" />
            Guardar esta vista…
            {pref.guardadas.length > 0 && <span className="ml-auto text-xs font-normal tabular-nums text-slate-500 dark:text-slate-400">{`${pref.guardadas.length} de ${TOPE_DE_VISTAS}`}</span>}
          </button>
        ) : (
          <p className="px-1 text-xs leading-relaxed text-slate-600 dark:text-slate-300">{`Ya tienes ${TOPE_DE_VISTAS} vistas guardadas, que es el tope. Borra una para guardar otra.`}</p>
        )}
      </div>
    </div>
  );
}
